import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import { SkeletonCard, SkeletonTable } from '../components/common/Skeleton';
import { Users, Copy, Check, Gift, DollarSign, Award, ArrowUpRight, Network } from 'lucide-react';

const ReferralDashboard = () => {
  const [treeData, setTreeData] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [activeLevelTab, setActiveLevelTab] = useState(1);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchReferrals = async () => {
    try {
      const resTree = await axiosClient.get('/referral/my-tree');
      setTreeData(resTree.data || null);

      const resEarn = await axiosClient.get('/referral/my-earnings');
      setEarnings(resEarn.data.earnings || []);
    } catch (err) {
      console.error('[Referral Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  const handleCopyLink = () => {
    const link = treeData?.referralLink || `${window.location.origin}/?ref=${treeData?.referralCode || ''}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentLevelMembers = treeData?.levels ? treeData.levels[`level${activeLevelTab}`] || [] : [];
  const levelRates = treeData?.rates || { level1: 4, level2: 3, level3: 2, level4: 1, level5: 2 };

  return (
    <div className="space-y-6">
      {/* Referral Link & Title Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <Gift className="w-5 h-5 text-[#F59E0B]" />
            <h2 className="text-lg font-extrabold text-[#FFFFFF]">Referral Program & Downlines</h2>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Build a downline team and earn automated referral commissions — your 5-level commission window slides as your downline grows infinitely.
          </p>
        </div>

        {/* Copy Referral Link Widget — always active for every user */}
        <div className="flex items-center space-x-2 bg-[#09090B] p-2.5 rounded-xl border border-[#27272A] shrink-0">
          <span className="font-mono text-xs text-[#F59E0B] truncate max-w-[200px] sm:max-w-[280px]">
            {treeData?.referralLink || `${window.location.origin}/?ref=${treeData?.referralCode || '...'}`}
          </span>
          <button
            onClick={handleCopyLink}
            className="px-3 py-1.5 rounded-lg bg-[#F97316] hover:bg-[#FB923C] text-white text-xs font-bold transition-colors flex items-center space-x-1 shrink-0"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-white" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">
                  {activeLevelTab === 1 ? 'Direct Referrals (Level 1)' : `Level ${activeLevelTab} Referrals`}
                </span>
                <Users className="w-4 h-4 text-[#F97316] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#FFFFFF] mt-2">
                {treeData?.levels ? (treeData.levels[`level${activeLevelTab}`] || []).length : 0} Members
              </p>
              <p className="text-[10px] text-[#F59E0B] mt-1">
                Commission Rate: {levelRates[`level${activeLevelTab}`] || 0}%
              </p>
            </Card>

            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Total Downline Team</span>
                <Network className="w-4 h-4 text-[#3B82F6] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#3B82F6] mt-2">{treeData?.summary?.totalTeam || 0} Members</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">Active downline network</p>
            </Card>

            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Total Referral Commissions</span>
                <DollarSign className="w-4 h-4 text-[#10B981] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#10B981] mt-2">${treeData?.summary?.totalReferralEarned ? treeData.summary.totalReferralEarned.toFixed(2) : '0.00'}</p>
              <p className="text-[10px] text-[#10B981] mt-1">Credited directly to wallet balance</p>
            </Card>
          </>
        )}
      </div>

      {/* Level Tabbed Tree Breakdown */}
      <Card 
        title="Downline Team Tree" 
        subtitle="Select a level tab below to inspect downline team members and earnings generated"
      >
        {/* Level Tabs */}
        <div className="flex flex-wrap items-center gap-2 pb-4 mb-4 border-b border-[#27272A]">
          {Array.from({ length: treeData?.maxAvailableLevels !== undefined ? treeData.maxAvailableLevels : 5 }, (_, i) => i + 1).map((lvl) => {
            const count = treeData?.levels ? (treeData.levels[`level${lvl}`] || []).length : 0;
            const rate = levelRates[`level${lvl}`] || 0;
            const isActive = activeLevelTab === lvl;

            return (
              <button
                key={lvl}
                onClick={() => setActiveLevelTab(lvl)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all duration-200 flex items-center space-x-2 ${
                  isActive
                    ? 'bg-[#F97316] text-[#FFFFFF] shadow-[0_2px_15px_rgba(249,115,22,0.35)]'
                    : 'bg-[#09090B] text-[#9CA3AF] hover:bg-[#1A1A1E] hover:text-[#FFFFFF] border border-[#27272A]'
                }`}
              >
                <span>Level {lvl} ({rate}%)</span>
                <span className={`px-1.5 py-0.5 rounded-md text-[10px] ${isActive ? 'bg-white/20 text-white' : 'bg-[#27272A] text-[#9CA3AF]'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Selected Level Table with Skeleton View */}
        {loading ? (
          <SkeletonTable rows={4} cols={5} />
        ) : currentLevelMembers.length === 0 ? (
          <div className="p-8 text-center space-y-2">
            <Users className="w-8 h-8 text-[#9CA3AF]/40 mx-auto" />
            <p className="text-xs text-[#9CA3AF]">No team members joined under Level {activeLevelTab} yet.</p>
            <p className="text-[10px] text-[#9CA3AF]">Share your referral link to invite users to Level 1.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">Member Name</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">MT5 Accounts</th>
                  <th className="py-3 px-4">Joined Date</th>
                  <th className="py-3 px-4 text-right">Earnings Generated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {currentLevelMembers.map((m) => (
                  <tr key={m.id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4 font-bold text-[#FFFFFF]">
                      {m.name} <span className="font-mono text-[10px] text-[#F59E0B]">({m.userIdTag})</span>
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {m.email}
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge status={m.status} label={m.status} />
                    </td>
                    <td className="py-3.5 px-4 text-[#FFFFFF] font-semibold">
                      {m.mt5Count} Accounts
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {new Date(m.joinedAt).toLocaleDateString()}
                    </td>
                    <td className="py-3.5 px-4 text-right font-bold text-[#10B981]">
                      +${m.totalEarnedFromUser ? m.totalEarnedFromUser.toFixed(2) : '0.00'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Referral Commission Audit History */}
      <Card title="Referral Commission Payout Log" subtitle="Every multi-level commission automatically credited to your wallet">
        {loading ? (
          <SkeletonTable rows={3} cols={5} />
        ) : earnings.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">No referral commission payout records found yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">Source Trader</th>
                  <th className="py-3 px-4">Referral Level</th>
                  <th className="py-3 px-4">Commission Rate (%)</th>
                  <th className="py-3 px-4">Commission Earned</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {earnings.map((e) => (
                  <tr key={e._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4 font-bold text-[#FFFFFF]">
                      {e.sourceUserId?.name || 'Downline Trader'} ({e.sourceUserId?.email})
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="px-2 py-0.5 rounded-md bg-[#F97316]/20 text-[#F97316] font-bold text-xs">
                        Level {e.level}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#F59E0B]">
                      {e.commissionRate}%
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#10B981]">
                      +${e.commissionAmount.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge status={e.status === 'credited' ? 'approved' : 'pending'} label={e.status} />
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {new Date(e.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReferralDashboard;
